import React, { useState, useEffect } from 'react';
import { 
  Form, 
  Button, 
  Alert, 
  Spinner,
  Container,
  Row,
  Col,
  Card
} from 'react-bootstrap';
import { FaTrash } from 'react-icons/fa';
import axios from 'axios';
import api from '../../services/api';
import Layout from '../../components/Layout';
import { baseURL,imageUrl } from '../../services/api';
import PageHeader from '../../components/PageHeader';
import TopBar from '../../components/TopBar';
const SettingsForm = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    dark_theme_color: '#2c3e50',
    light_theme_color: '#ffffff',
    print_theme_color: '#000000',
    address: '',
    phone_number: '',
    gst_number: '',
    logo: null
  });
  const [previewLogo, setPreviewLogo] = useState(null);

  const handleLogoChange = (file) => {
    setFormData(prev => ({ ...prev, logo: file }));
    if (file) {
      setPreviewLogo(URL.createObjectURL(file));
    } else {
      setPreviewLogo(null);
    }
  };
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  // Fetch existing settings
  useEffect(() => {
    api.get('/settings').then(res => {
      if (res.data) {
        setFormData(prev => ({ ...prev, ...res.data }));
        if (res.data.logo) {
          setPreviewLogo(`${imageUrl}/${res.data.logo}`);
        }
      }
    });
  }, []);

  const handleChange = e => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileChange = e => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        setErrors({ logo: 'Please select a valid image file' });
        return;
      }
      // Validate file size
      if (file.size > 2048 * 1024) { // 2MB
        setErrors({ logo: 'Image size should be less than 2MB' });
        return;
      }
      handleLogoChange(file);
      setErrors({ logo: '' });
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);
    setMessage('');
    setErrors({});

    try {
      // Create form data
      const data = new FormData();
      Object.entries(formData).forEach(([key, val]) => {
        if (val !== null && val !== undefined && val !== '') {
          data.append(key, val);
        }
      });
      
      // Add logo if selected
      if (formData.logo) {
        data.append('logo', formData.logo);
      }

      // Log the form data for debugging
      console.log('Form data:', Object.fromEntries(data.entries()));

      const res = await api.post('/settings', data, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      // Reset form after successful submission
      if (res.status === 200) {
        setMessage(res.data.message);
    
      }
    } catch (err) {
      console.error('Error:', err);
      if (err.response?.status === 422) {
        setErrors(err.response.data.errors);
      } else {
        setMessage('Failed to update settings');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <TopBar/>
      <PageHeader title="Company Settings" />
      <Container fluid className="mt-4">
        <Card>
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              {message && <Alert variant="info" className="mb-3">{message}</Alert>}
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Company Name</Form.Label>
                    <Form.Control
                      type="text"
                      name="company_name"
                      value={formData.company_name}
                      onChange={handleChange}
                      isInvalid={!!errors.company_name}
                    />
                    <Form.Control.Feedback type="invalid">
                      {errors.company_name}
                    </Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Logo</Form.Label>
                    <div className="d-flex flex-column align-items-center">
                      <Form.Control
                        type="file"
                        name="logo"
                        onChange={handleFileChange}
                        accept="image/*"
                        className="mb-3"
                        style={{ maxWidth: '300px' }}
                      />
                      {previewLogo ? (
                        <div className="position-relative mt-3">
                          <img
                            src={previewLogo}
                            alt="Logo Preview"
                            className="img-thumbnail"
                            style={{ 
                              maxWidth: '300px', 
                              maxHeight: '200px',
                              objectFit: 'contain'
                            }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => {
                              handleLogoChange(null);
                              setErrors(prev => ({ ...prev, logo: '' }));
                            }}
                            className="position-absolute top-0 end-0 mt-2 me-2 p-1"
                            style={{
                              backgroundColor: 'rgba(220, 53, 69, 0.9)',
                              borderColor: 'rgba(220, 53, 69, 0.9)',
                              borderRadius: '50%'
                            }}
                          >
                            <FaTrash style={{ fontSize: '1.2rem' }} />
                          </Button>
                        </div>
                      ) : (
                        <div className="text-muted text-center">
                          <i className="bi bi-image-fill" style={{ fontSize: '4rem' }}></i>
                          <p className="mb-0">Click to upload logo</p>
                        </div>
                      )}
                    </div>
                    {errors.logo && (
                      <Form.Text className="text-danger text-center">
                        {errors.logo}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Dark Theme Color</Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        name="dark_theme_color"
                        value={formData.dark_theme_color}
                        onChange={handleChange}
                        className="me-2"
                        style={{ width: '100px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.dark_theme_color}
                        onChange={handleChange}
                        name="dark_theme_color"
                        className="flex-grow-1"
                        readOnly
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Light Theme Color</Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        name="light_theme_color"
                        value={formData.light_theme_color}
                        onChange={handleChange}
                        className="me-2"
                        style={{ width: '100px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.light_theme_color}
                        onChange={handleChange}
                        name="light_theme_color"
                        className="flex-grow-1"
                        readOnly
                      />
                    </div>
                  </Form.Group>
                </Col>
                <Col md={4}>
                  <Form.Group className="mb-3">
                    <Form.Label>Print Theme Color</Form.Label>
                    <div className="d-flex align-items-center">
                      <Form.Control
                        type="color"
                        name="print_theme_color"
                        value={formData.print_theme_color}
                        onChange={handleChange}
                        className="me-2"
                        style={{ width: '100px' }}
                      />
                      <Form.Control
                        type="text"
                        value={formData.print_theme_color}
                        onChange={handleChange}
                        name="print_theme_color"
                        className="flex-grow-1"
                        readOnly
                      />
                    </div>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      rows={3}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label>GST Number</Form.Label>
                    <Form.Control
                      type="text"
                      name="gst_number"
                      value={formData.gst_number}
                      onChange={handleChange}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="d-flex justify-content-end">
                <Button
                  variant="primary"
                  type="submit"
                  disabled={loading}
                  className="me-2"
                >
                  {loading ? (
                    <>
                      <Spinner
                        as="span"
                        animation="border"
                        size="sm"
                        role="status"
                        aria-hidden="true"
                        className="me-2"
                      />
                      Saving...
                    </>
                  ) : (
                    'Save Settings'
                  )}
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => window.history.back()}
                >
                  Cancel
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </Layout>
   
  );
};

export default SettingsForm;
